import React, { useContext, useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "@/App";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  LayoutDashboard, ShieldAlert, Boxes, Truck, Building2, ArrowDownToLine,
  ArrowUpFromLine, Receipt, Undo2, ClipboardCheck, FileSpreadsheet, History, Settings, LogOut, PackageCheck, Sparkles, Users, Factory, Wallet, Landmark, Activity, Scale, ChevronDown
} from "lucide-react";
import { BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const MENU = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/reconciliation", label: "Neraca & Rekonsiliasi", icon: Scale, testid: "nav-reconciliation" },
  { to: "/checker", label: "Stock Checker", icon: ShieldAlert, testid: "nav-checker" },
  { to: "/system-health", label: "Pemeriksaan Sistem", icon: Activity, testid: "nav-system-health" },
  { to: "/products", label: "Master Barang", icon: Boxes, testid: "nav-products" },
  { to: "/fleet", label: "Sales / Armada", icon: Truck, testid: "nav-fleet" },
  { to: "/customers", label: "Pelanggan", icon: Users, testid: "nav-customers" },
  { to: "/suppliers", label: "Supplier", icon: Building2, testid: "nav-suppliers" },
  { label: "Produksi", icon: Factory, testid: "nav-production", children: [
    { to: "/production", label: "Rencana Produksi", end: true, testid: "nav-prod-plans" },
    { to: "/production/materials", label: "Pengambilan Bahan", testid: "nav-prod-materials" },
    { to: "/production/packages", label: "Paket & Hasil", testid: "nav-prod-packages" },
    { to: "/production/waste", label: "Reject & Waste", testid: "nav-prod-waste" },
    { to: "/production/checker", label: "Checker Produksi", testid: "nav-prod-checker" },
    { to: "/production/reports", label: "Laporan & Upah", testid: "nav-prod-reports" },
  ]},
  { to: "/inbound", label: "Barang Masuk", icon: ArrowDownToLine, testid: "nav-inbound" },
  { to: "/outbound", label: "Barang Keluar", icon: ArrowUpFromLine, testid: "nav-outbound" },
  { to: "/sales", label: "Penjualan", icon: Receipt, testid: "nav-sales" },
  { to: "/receivables", label: "Piutang", icon: Wallet, testid: "nav-receivables" },
  { to: "/returns", label: "Retur", icon: Undo2, testid: "nav-returns" },
  { to: "/opname", label: "Stock Opname", icon: ClipboardCheck, testid: "nav-opname" },
  { to: "/opening-balance", label: "Saldo Awal", icon: Landmark, testid: "nav-opening-balance" },
  { to: "/reports", label: "Laporan", icon: FileSpreadsheet, testid: "nav-reports" },
  { to: "/analytics", label: "Analisis Sales", icon: BarChart3, testid: "nav-analytics" },
  { to: "/audit", label: "Audit Trail", icon: History, testid: "nav-audit" },
  { to: "/settings", label: "Pengaturan", icon: Settings, testid: "nav-settings" },
];

const roleLabels = { owner: "Owner", admin: "Admin Gudang", sales: "Sales Kanvaser" };
const roleBadge = { owner: "bg-emerald-100 text-emerald-800 border-emerald-300", admin: "bg-blue-100 text-blue-800 border-blue-300", sales: "bg-indigo-100 text-indigo-800 border-indigo-300" };

export default function Layout() {
  const { user, setUser, refresh } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [alertCount, setAlertCount] = useState(0);
  const [seeding, setSeeding] = useState(false);
  const isSales = user?.role === "sales";
  const menu = isSales ? MENU.filter((m) => ["/sales", "/returns"].includes(m.to)) : MENU;
  const [openGroups, setOpenGroups] = useState({ Produksi: true });

  useEffect(() => {
    if (isSales && !["/sales", "/returns"].some((p) => location.pathname.startsWith(p))) navigate("/sales", { replace: true });
  }, [isSales, location.pathname, navigate]);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await api.get("/checker/alerts");
        setAlertCount(r.data.length);
      } catch (e) {
        console.error("Gagal memuat alert checker:", e);
      }
    };
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { console.error("Gagal logout:", e); }
    setUser(null);
    navigate("/login", { replace: true });
  };

  const runSeed = async () => {
    setSeeding(true);
    try {
      const r = await api.post("/seed");
      if (r.data.seeded) toast.success("Demo data berhasil ditambahkan");
      else toast.info(r.data.message || "Sudah ada data");
      await refresh();
      window.location.reload();
    } catch (e) {
      toast.error("Gagal seed data");
    } finally { setSeeding(false); }
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 bg-slate-900 text-slate-200 flex flex-col sticky top-0 h-screen">
        <div className="p-5 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded bg-emerald-500 grid place-items-center">
              <PackageCheck className="h-5 w-5 text-slate-900" />
            </div>
            <div>
              <div className="font-display font-bold text-white text-lg leading-none">GudangKu</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">Warehouse + Armada</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-3">
          {menu.map((m) => m.children ? (
            <div key={m.label}>
              <button
                type="button"
                onClick={() => setOpenGroups({ ...openGroups, [m.label]: !openGroups[m.label] })}
                data-testid={m.testid}
                className={`w-full flex items-center gap-3 px-5 py-2.5 text-sm transition-colors border-l-2 ${
                  location.pathname.startsWith("/production")
                    ? "text-white bg-slate-800/60 border-emerald-500"
                    : "text-slate-400 hover:text-white hover:bg-slate-800/60 border-transparent"
                }`}
              >
                <m.icon className="h-4 w-4" />
                <span className="flex-1 text-left">{m.label}</span>
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${openGroups[m.label] ? "" : "-rotate-90"}`} />
              </button>
              {openGroups[m.label] && m.children.map((c) => (
                <NavLink
                  key={c.to}
                  to={c.to}
                  end={c.end}
                  data-testid={c.testid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 pl-11 pr-5 py-2 text-[13px] transition-colors ${
                      isActive ? "bg-slate-800 text-white border-l-2 border-emerald-500" : "text-slate-500 hover:text-white hover:bg-slate-800/60 border-l-2 border-transparent"
                    }`
                  }
                >
                  {c.label}
                </NavLink>
              ))}
            </div>
          ) : (
            <NavLink
              key={m.to}
              to={m.to}
              end={m.end}
              data-testid={m.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-5 py-2.5 text-sm transition-colors ${
                  isActive
                    ? "bg-slate-800 text-white border-l-2 border-emerald-500"
                    : "text-slate-400 hover:text-white hover:bg-slate-800/60 border-l-2 border-transparent"
                }`
              }
            >
              <m.icon className="h-4 w-4" />
              <span className="flex-1">{m.label}</span>
              {m.to === "/checker" && alertCount > 0 && (
                <Badge className="bg-rose-500 hover:bg-rose-500 text-white h-5 px-1.5 text-[10px]">{alertCount}</Badge>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-800">
          <div className="flex items-center gap-3 mb-3">
            {user?.picture ? (
              <img src={user.picture} alt="" className="h-9 w-9 rounded-full" />
            ) : (
              <div className="h-9 w-9 rounded-full bg-slate-700" />
            )}
            <div className="min-w-0 flex-1">
              <div className="text-sm text-white truncate" data-testid="user-name">{user?.name}</div>
              <div className="text-[10px] text-slate-500 truncate">{user?.email}</div>
            </div>
          </div>
          <div className="flex gap-2">
            <Badge className={`${roleBadge[user?.role] || "bg-slate-700"} font-normal`} data-testid="user-role">
              {roleLabels[user?.role] || user?.role}
            </Badge>
            <Button
              size="sm"
              variant="ghost"
              onClick={logout}
              className="ml-auto text-slate-400 hover:text-white h-7 px-2"
              data-testid="logout-btn"
            >
              <LogOut className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-10 bg-white/80 backdrop-blur border-b border-slate-200 px-8 py-3 flex items-center gap-3">
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500">
            {new Date().toLocaleDateString("id-ID", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}
          </div>
          <div className="ml-auto flex items-center gap-2">
            {!isSales && (
            <Button
              size="sm"
              variant="outline"
              onClick={runSeed}
              disabled={seeding}
              className="h-8 gap-1.5 text-xs"
              data-testid="seed-btn"
            >
              <Sparkles className="h-3.5 w-3.5" />
              {seeding ? "Memuat..." : "Isi Demo Data"}
            </Button>
            )}
          </div>
        </header>
        <main className="flex-1 p-8 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
